import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "antd";
import { citiesApi } from "api/city";
import LabeledDateInput from "components/Form/LabeledDateInput";
import LabeledInput from "components/Form/LabeledInput";
import LabeledSelectInput from "components/Form/LabeledSelectInput";
import { useNotificationContext } from "contexts/NotificationContext";
import { ILoadingData } from "models";
import { ICity } from "models/city";
import { IDriverInput } from "models/driver";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import createDriverValidator from "validators/createDriver.validator";
import "react-html5-camera-photo/build/css/index.css";
import LabeledUploadInput from "components/Form/LabeledUploadInput";
import LabeledUploadsInput from "components/Form/LabeledUploadsInput";
import { RcFile } from "antd/es/upload";

interface IProps {
  onDriverInfoSubmit: (input: IDriverInput) => void;
  stepper: number;
  handleBackStepper: () => void;
  handleUploadDriverImage: (file: RcFile) => Promise<string | undefined>;
  handleUploadDriverLicenseImage: (file: RcFile) => Promise<string | undefined>;
  handleUploadInsuranceSubscriptionImage: (
    file: RcFile
  ) => Promise<string | undefined>;
  handleUploadEmploymentAgreementImage: (
    file: RcFile
  ) => Promise<string | undefined>;
  handleUploadMaheberSupportImage: (
    file: RcFile
  ) => Promise<string | undefined>;
  handleUploadDriverIdImage: (file: RcFile) => Promise<string | undefined>;
  handleUploadOwnerIdImage: (file: RcFile) => Promise<string | undefined>;
  handleUploadBoloImage: (file: RcFile) => Promise<string | undefined>;
  handleUploadBusinessLicenseImage: (
    file: RcFile
  ) => Promise<string | undefined>;
}

export default function DriverInfoStepper({
  onDriverInfoSubmit,
  handleBackStepper,
  stepper,
  handleUploadDriverImage,
  handleUploadDriverLicenseImage,
  handleUploadInsuranceSubscriptionImage,
  handleUploadEmploymentAgreementImage,
  handleUploadMaheberSupportImage,
  handleUploadDriverIdImage,
  handleUploadOwnerIdImage,
  handleUploadBoloImage,
  handleUploadBusinessLicenseImage,
}: IProps) {
  const { showError } = useNotificationContext();
  const [cities, setCities] = useState<ILoadingData<ICity>>({
    data: [],
    loading: false,
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<IDriverInput>({
    resolver: yupResolver(createDriverValidator),
  });
  console.log(errors);

  useEffect(() => {
    citiesApi(1, 100, "").then(({ data, error }) => {
      if (data) setCities({ data: data.data, loading: false });
      else showError(error!.message);
    });
  }, []); // eslint-disable-line

  return (
    <form
      onSubmit={handleSubmit(onDriverInfoSubmit)}
      className={`w-full ${stepper === 1 ? "flex" : "hidden"} flex-wrap`}
    >
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver First Name"
          type="text"
          control={control}
          name="first_name"
          placeholder="Driver First Name"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Middle Name"
          type="text"
          control={control}
          name="middle_name"
          placeholder="Driver Middle Name"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Last Name"
          type="text"
          control={control}
          name="last_name"
          placeholder="Driver Last Name"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Full Name (Local)"
          type="text"
          control={control}
          name="local_full_name"
          placeholder="Driver Full Name (Local)"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Phone Number"
          type="tel"
          control={control}
          name="phone"
          placeholder="Driver Phone Number"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Backup Phone Number"
          type="tel"
          control={control}
          name="backup_phone"
          placeholder="Driver Backup Phone Number"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Email (Optional)"
          type="email"
          control={control}
          name="email"
          placeholder="Driver Email"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledSelectInput
          label="City"
          control={control}
          loading={cities.loading}
          name="city_id"
          placeholder="Select City"
          options={cities.data.map((value) => ({
            value: value.id,
            label: value.name,
          }))}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledSelectInput
          label="Gender"
          control={control}
          name="gender"
          placeholder="Select Gender"
          options={[
            { label: "Male", value: "male" },
            { label: "Female", value: "female" },
          ]}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledInput
          label="Driver Tin Number"
          type="text"
          control={control}
          name="tin_number"
          placeholder="Driver Tin Number"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledDateInput
          label="Driver Date of Birth"
          control={control}
          name="date_of_birth"
          placeholder="Driver Date of Birth"
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3"></div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="image"
          placeholder="Select Driver Image"
          label="Driver Image"
          action={handleUploadDriverImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="driving_license_image"
          placeholder="Select Driver License Image"
          label="Driver License Image"
          action={handleUploadDriverLicenseImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="insurance_subscription_image"
          placeholder="Select Driver Insurance image"
          label="Driver Insurance Image"
          action={handleUploadInsuranceSubscriptionImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="employment_agreement_image"
          placeholder="Select Driver employment agreement image"
          label="Driver Employment Agreement Image"
          action={handleUploadEmploymentAgreementImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="maheber_support_image"
          placeholder="Select Maheber Support Image"
          label="Maheber Support Image"
          action={handleUploadMaheberSupportImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="driver_id_image"
          placeholder="Select Driver Id Image"
          label="Driver Id Image"
          action={handleUploadDriverIdImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="owner_id_image"
          placeholder="Select Owner Id Image"
          label="Owner Id Image"
          action={handleUploadOwnerIdImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/3">
        <LabeledUploadInput
          control={control}
          name="bolo_image"
          placeholder="Select Bolo Image"
          label="Bolo Image"
          action={handleUploadBoloImage}
        />
      </div>
      <div className="w-full px-4 pt-5 md:w-1/2 flex items-center gap-x-4">
        <LabeledUploadsInput
          control={control}
          name="business_license_images"
          placeholder="Select Driver Business License Image"
          action={handleUploadBusinessLicenseImage}
          label="Driver Business License Images"
        />
      </div>

      <div className="w-full flex mt-8 justify-between">
        <Button
          onClick={handleBackStepper}
          type="primary"
          className="py-2 px-8 rounded-md float-right h-auto font-medium w-fit shadow-none"
        >
          Back
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          className="py-2 px-8 rounded-md float-right h-auto font-medium w-fit shadow-none"
        >
          Next
        </Button>
      </div>
    </form>
  );
}
